/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import Subject from '../../rx/Subject';
import ChannelState from '../channels/ChannelState';
import Dimension from '../../video/Dimension';
import EndPoint, {IStream, SubscribeStatus} from '../discovery/EndPoint';
import {IOnRealTimeSubscribeRequestCallback} from '../channels/IOnRealTimeSubscribeRequestCallback';
import {IOnRealTimeSubscribeResponseCallback} from '../channels/IOnRealTimeSubscribeResponseCallback';
import VideoTelemetry from '../../video/VideoTelemetry';
import {IRtcMonitorStatistic, IRtcStatistic} from '../../rtc/RtcConnectionMonitor';
import SDK from '../SDK';
import assertUnreachable from '../../lang/assertUnreachable';
import DisposableList from '../../lang/DisposableList';
import {BitsPerSecond, Millisecond} from '../../units/Units';

const defaultTargetLag = 0;

export type ChannelContextOptions = {
  targetLag?: number;
  onRealTimeSubscribeRequestCallback?: IOnRealTimeSubscribeRequestCallback;
  onRealTimeSubscribeResponseCallback?: IOnRealTimeSubscribeResponseCallback;
};

export default class ChannelContext {
  channelInitialization: Date;
  disposables: DisposableList;
  channelDisposables: DisposableList;
  rendererDisposables: DisposableList;
  videoElement: Subject<HTMLVideoElement | null>;
  state: Subject<ChannelState>;
  exists: Subject<boolean | null>;
  autoMuted: Subject<boolean>;
  autoPaused: Subject<boolean>;
  authorized: Subject<boolean>;
  online: Subject<boolean>;
  loading: Subject<boolean>;
  playing: Subject<boolean>;
  standby: Subject<boolean>;
  stopped: Subject<boolean>;
  targetLag: Subject<Millisecond>;
  lag: Subject<Millisecond | undefined>;
  bitrateLimit: Subject<BitsPerSecond>;
  resolution: Subject<Dimension>;
  failureCount: Subject<number>;
  endPoint: Subject<EndPoint | null>;
  onRealTimeSubscribeRequestCallback: IOnRealTimeSubscribeRequestCallback | null;
  onRealTimeSubscribeResponseCallback: IOnRealTimeSubscribeResponseCallback | null;
  stream: Subject<IStream | null>;
  rtcStatistics: Subject<IRtcMonitorStatistic | null>;
  videoTelemetry: VideoTelemetry | null;
  rtcAudioStatistic: IRtcStatistic | null;
  rtcVideoStatistic: IRtcStatistic | null;
  clearFailureCountTimeout: number;

  constructor(options?: ChannelContextOptions) {
    this.disposables = new DisposableList();
    this.channelDisposables = new DisposableList();
    this.rendererDisposables = new DisposableList();
    this.channelInitialization = new Date();
    this.videoElement = new Subject<HTMLVideoElement | null>(null);
    this.state = new Subject<ChannelState>(ChannelState.Initializing);
    this.exists = new Subject<boolean | null>(null);
    this.autoMuted = new Subject<boolean>(false);
    this.autoPaused = new Subject<boolean>(false);
    this.authorized = new Subject<boolean>(true);
    this.online = new Subject<boolean>(true);
    this.loading = new Subject<boolean>(false);
    this.playing = new Subject<boolean>(false);
    this.standby = new Subject<boolean>(false);
    this.stopped = new Subject<boolean>(false);
    this.targetLag = new Subject<Millisecond>(options?.targetLag ?? defaultTargetLag);
    this.lag = new Subject<Millisecond | undefined>(undefined);
    this.bitrateLimit = new Subject<BitsPerSecond>(0);
    this.resolution = new Subject<Dimension>(Dimension.empty);
    this.failureCount = new Subject<number>(0);
    this.endPoint = new Subject<EndPoint | null>(null);
    this.onRealTimeSubscribeRequestCallback = options?.onRealTimeSubscribeRequestCallback ?? null;
    this.onRealTimeSubscribeResponseCallback = options?.onRealTimeSubscribeResponseCallback ?? null;
    this.stream = new Subject<IStream | null>(null);
    this.rtcStatistics = new Subject<IRtcMonitorStatistic | null>(null);
    this.videoTelemetry = null;
    this.rtcAudioStatistic = null;
    this.rtcVideoStatistic = null;
    this.clearFailureCountTimeout = 0;
  }

  get streamId(): string {
    const stream = this.stream.value;

    if (!stream) {
      return '-';
    }

    return stream.streamId;
  }

  applyStatus(status: SubscribeStatus): void {
    switch (status) {
      case 'ok':
        break;
      case 'unauthorized':
      case 'geo-restricted':
      case 'geo-blocked':
        this.authorized.value = false;
        this.failureCount.value = 0;
        this.playing.value = false;
        this.standby.value = true;
        this.stopped.value = false;
        this.stream.value = null;

        return;
      case 'no-stream':
      case 'not-found':
        this.failureCount.value = 0;
        this.playing.value = false;
        this.standby.value = true;
        this.stopped.value = false;
        this.stream.value = null;

        return;
      default:
        this.failureCount.value++;
        this.playing.value = false;
        this.standby.value = true;
        this.stopped.value = false;
        this.stream.value = null;

        return;
    }
  }

  applySessionAndStreamPropertiesToVideoElement(): void {
    if (this.videoElement.value && this.videoElement.value.dataset) {
      this.videoElement.value.dataset.sessionId = SDK.clientSessionId;
      this.videoElement.value.dataset.streamId = this.streamId;
    }
  }

  mapSubscribeStatusToChannelStatus(status: SubscribeStatus): ChannelState {
    switch (status) {
      case 'ok':
        return ChannelState.Starting;
      case 'no-stream':
      case 'not-found':
        return ChannelState.StandBy;
      case 'geo-restricted':
        return ChannelState.GeoRestricted;
      case 'geo-blocked':
        return ChannelState.GeoBlocked;
      case 'unauthorized':
        return ChannelState.Unauthorized;
      case 'capacity':
      case 'rate-limited':
      case 'timeout':
        return ChannelState.Recovering;
      case 'forbidden-tag':
        return ChannelState.ConfigurationError;
      case 'port-in-range-not-found':
        return ChannelState.TransientConfigurationError;
      case 'failed':
        return ChannelState.Error;
      default:
        assertUnreachable(status);
    }
  }

  mapSubscribeStatusToExists(status: SubscribeStatus): boolean | null {
    switch (status) {
      case 'ok':
      case 'no-stream':
      case 'geo-restricted':
      case 'geo-blocked':
      case 'forbidden-tag':
      case 'port-in-range-not-found':
        return true;
      case 'not-found':
        return false;
      case 'unauthorized':
      case 'timeout':
      case 'rate-limited':
      case 'capacity':
      case 'failed':
        return this.exists.value;
      default:
        assertUnreachable(status);
    }
  }
}